import React, { useState, useEffect, useRef } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate, useOutletContext } from 'react-router-dom';
// icons
import { AiOutlineEye } from 'react-icons/ai';
import { FiEdit } from 'react-icons/fi';
//helpers
import { convertDateToDDMMYYYY } from 'helpers';
//api
import { getAllInvoicesNotPaginated } from 'api/invoice';
import { getInvoices } from 'api/pathForHook/invoice';
//utils
import useHandleSearch from 'utils/customHooks/useHandleSearch';
import { formatCurrency } from 'utils/currencyFormatter';
//context
import { usePermissions } from 'components/backoffice/PermissionsContext';
//export
import { createFilesToExport } from 'utils/backoffice/exportPDFExcel';
//components
import CustomTable from 'components/backoffice/customTable';
import Button from 'components/backoffice/buttonCustom';
import Title from 'components/backoffice/texts/title';
import CustomCheckbox from 'components/backoffice/customCheckbox';
import ExportModal from 'components/backoffice/modals/ExportModal';
import InvoiceModal from 'components/backoffice/modals/InvoiceModal';
import FiltersTableInvoices from './FiltersTableInvoices';
import StatusBudget from 'components/budget/StatusBudget';

const Invoices = () => {
  const userPermissions = usePermissions();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [page, setPage] = useState(1);
  const [openWatchModal, setOpenWatchModal] = useState(false);
  const [openExportModal, setOpenExportModal] = useState(false);
  const [itemToShow, setItemToShow] = useState({});
  const [formFilter, setFormFilter] = useState({
    search: '',
    status: ''
  })
  const [selectedRows, setSelectedRows] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [responseInfo, setResponseInfo] = useState({});
  const [elements, setElements] = useState(0);
  const tableRef = useRef();
  const { setTitle } = useOutletContext()
  const { t } = useTranslation(['backoffice']);

  const { fetchData, stateData, loading } = useHandleSearch();

  const getAllInvoices = async () => {
    try {
      let payload = {
        page,
        page_size: 10,
        search: formFilter.search || '',
        status: formFilter.status || '',
        fields: 'id,identifier,name,updated,user,order,amount,total,status,company'
      }
      await fetchData(getInvoices(), payload);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    setTitle(t('facturas'))
    return () => {
      setTitle('');
    };
  }, [setTitle])

  useEffect(() => {
    const { results, errors, totalPages, noResults, totalElements } = stateData;
    if (results !== null) {
      setInvoices(results);
      setElements(totalElements)
      setResponseInfo({ errors, totalPages, noResults });
    } else {
      setInvoices(null);
      setResponseInfo({ errors })
    }
  }, [stateData])

  useEffect(() => {
    getAllInvoices();
  }, [page, formFilter]);


  const handleClear = () => {
    setFormFilter({
      search: '',
      status: ''
    })
    setPage(1);
    setResponseInfo(prevResponseInfo => ({ ...prevResponseInfo, noResults: false }));
  };

  const handleCreate = () => {
    navigate('/intranet/invoices/create');
  }

  const handleEdit = (id) => {
    navigate(`/intranet/invoices/edit/${id}`);
  }

  const handleWatchItem = (item) => {
    setItemToShow(item);
    setOpenWatchModal(true);
  };

  const toggleSelectAll = async (e) => {
    if (e.target.checked) {
      const { data, status } = await getAllInvoicesNotPaginated();
      if (status === 200) {
        setSelectedRows(data.map(row => row));
        setAllOrders(data)
      }
    } else {
      setSelectedRows([]);
      setAllOrders([])
    }
  };

  const toggleSelectRow = (item) => {
    setAllOrders([])
    setSelectedRows(prev =>
      prev.some((el) => el.id === item.id) ? prev.filter(rowId => rowId.id !== item.id) : [...prev, item]
    );
  };

  const colsInvoices = [
    <CustomCheckbox
      onChange={toggleSelectAll}
      checked={selectedRows.length === allOrders.length && allOrders.length > 0} />,
    t('numberInvoice'), t('name'), t('date'), t('user'), t('order'), t('amount'), t('state'), t('actions')];

  const handleExport = async (format) => {
    const selectedData = allOrders.length > 0 ? allOrders : selectedRows;
    if (format === 'pdf') {
      createFilesToExport(t, selectedData, 'pdf', 'invoice');
    } else if (format === 'excel') {
      createFilesToExport(t, selectedData, 'xlsx', 'invoice');
    }
    setOpenExportModal(false);
    setSelectedRows([]);
  }

  const serializerOrderStatus = (status) => {
    switch (status) {
      case 'paid':
        return 'success';
      case 'notPaid':
        return 'danger';
      case 'cancelled':
        return 'danger';
      case 'expired':
        return 'warning';
      case 'pending':
        return 'info';
      case 'pendingPayment':
        return 'secondary';
      default:
        return 'info';
    }
  };

  return (
    <div className="d-lg-grid">
      <Title text={t('facturas')} />
      <Row className="d-flex w-md-100 mt-3">
        <Col xl={9} lg={12} md={12} xs={12} className="d-flex text-start">
          <FiltersTableInvoices
            formFilter={formFilter}
            setFormFilter={setFormFilter}
            handleClear={handleClear}
            t={t}
            page={page}
            setPage={setPage}
          />
        </Col>
        <Col xl={3} lg={12} md={12} xs={12} className="d-flex text-end align-bottom p-xl-0 justify-content-end pt-5 pt-xl-0 gap-3">
          {userPermissions.includes('invoice.__add') && (
            <>
              <Button color="primary" onClick={handleCreate} extraClass={'mt-1'}>
                {t('crear')}
              </Button>
              <Button variant="secondary" color="primary" onClick={() => setOpenExportModal(true)} extraClass={'mt-1'} disabled={selectedRows.length === 0}>
                {t('export')}
              </Button>
            </>
          )}
        </Col>
      </Row>
      <div className="table-responsive">
        <CustomTable
          cols={colsInvoices}
          noResults={responseInfo?.noResults}
          isLoading={loading}
          page={page}
          changePage={setPage}
          totalPages={responseInfo?.totalPages}
          ref={tableRef}
          selected={selectedRows.length}
          elements={elements}
        >
          {invoices?.map((inv) => {
            return <tr key={inv.id} data-id={inv.id}>
              <td>
                <CustomCheckbox
                  checked={selectedRows.some((el) => el.id === inv.id)}
                  onChange={() => toggleSelectRow(inv)} />
              </td>
              <td>{`#${inv.identifier}`}</td>
              <td>{inv.name}</td>
              <td>{convertDateToDDMMYYYY(inv.updated)}</td>
              <td>{inv.user}</td>
              <td>{inv.order.code ? `#${inv.order.code}` : '-'}</td>
              <td>{formatCurrency(inv.total)}</td>
              <td>
                <div className="d-flex justify-content-center">
                  <StatusBudget
                    text={t(`orderTrans.${inv.status}`)}
                    status={serializerOrderStatus(inv.status)}
                  />
                </div>
              </td>
              <td className="text-end">
                {userPermissions.includes('invoice.__view') && (
                  <AiOutlineEye className='style-icon-delete me-3' color="white" size={20} onClick={() => handleWatchItem(inv)} title={t('orderTrans.seeInvoice')} />
                )}
                {userPermissions.includes('invoice.__modify') && !['paid', 'cancelled', 'expired'].includes(inv.status) && (
                  <FiEdit className='style-icon-delete' color="white" size={20} onClick={() => handleEdit(inv.id)} title={t('orderTrans.editInvoice')} />
                )}
              </td>
            </tr>
          })}
        </CustomTable>
      </div>
      <InvoiceModal
        title={t('facturas').split('s')[0]}
        invoice={itemToShow}
        show={openWatchModal}
        setShow={setOpenWatchModal}
      />
      <ExportModal
        title={`${t('export')} ${t('facturas')}`}
        show={openExportModal}
        setShow={setOpenExportModal}
        handleSave={handleExport}
      />
    </div>
  );
};

export default Invoices;
